// src/pages/AdminEntityFormationPage.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { loadAllEntityFormationSubmissionsAdmin, deleteEntityFormationSubmission } from '../lib/intake/entityFormationSubmitApi';
import { loadAllEntityFormationDraftsAdmin } from '../lib/intake/entityFormationDraftApi';
import { deleteEntityFormationDraft } from '../lib/intake/entityFormationSubmitApi';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { EntityIntakeDefaults } from '../lib/intake/entityFormationDefaults';
import { ENTITY_INTAKE_SECTIONS, getVisibleFieldKeysForStep, type EntityIntakeFieldKey } from '../lib/intake/entityFormationFields';
import { US_STATES } from '../lib/usStates';
import {
  OWNER_TYPE_OPTIONS,
  type Option,
} from '../lib/intake/entityFormationOptions';
import { logDataAccess, logPrivacyEvent } from '../lib/audit/auditLogger';

interface SubmissionWithClient {
  id: string;
  type: 'submission' | 'draft';
  submissionId?: string;
  draftId?: string;
  createdByUid: string;
  createdByEmail: string;
  clientName: string;
  businessName: string;
  data: EntityIntakeDefaults;
  createdAt: Date | null;
  updatedAt: Date | null;
  submittedAt?: Date | null;
}

const AdminEntityFormationPage: React.FC = () => {
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<SubmissionWithClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!roleLoading && role !== 'admin') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    const loadForms = async () => {
      if (role !== 'admin') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Load both drafts and submissions in parallel
        const [allDrafts, allSubmissions] = await Promise.all([
          loadAllEntityFormationDraftsAdmin(),
          loadAllEntityFormationSubmissionsAdmin(),
        ]);
        
        // Helper function to get client name from user document
        const getClientName = async (uid: string, email: string): Promise<string> => {
          try {
            const userDocRef = doc(db, 'users', uid);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.displayName) {
                return userData.displayName;
              } else if (userData.firstName && userData.lastName) {
                return `${userData.firstName} ${userData.lastName}`;
              }
            }
          } catch (err) {
            console.warn('Could not load user info:', err);
          }
          return email;
        };

        // Transform drafts
        const draftsWithClient: SubmissionWithClient[] = await Promise.all(
          allDrafts.map(async (draft) => {
            const clientName = await getClientName(draft.createdByUid, draft.createdByEmail);
            return {
              id: draft.draftId,
              type: 'draft' as const,
              draftId: draft.draftId,
              createdByUid: draft.createdByUid,
              createdByEmail: draft.createdByEmail,
              clientName,
              businessName: draft.data.legal_business_name || 'Untitled',
              data: draft.data,
              createdAt: draft.createdAt?.toDate() || null,
              updatedAt: draft.updatedAt?.toDate() || null,
            };
          })
        );

        // Transform submissions
        const submissionsWithClient: SubmissionWithClient[] = await Promise.all(
          allSubmissions.map(async (submission) => {
            const clientName = await getClientName(submission.createdByUid, submission.createdByEmail);
            return {
              id: submission.submissionId,
              type: 'submission' as const,
              submissionId: submission.submissionId,
              createdByUid: submission.createdByUid,
              createdByEmail: submission.createdByEmail,
              clientName,
              businessName: submission.data.legal_business_name || 'Untitled',
              data: submission.data,
              createdAt: submission.createdAt?.toDate() || null,
              updatedAt: submission.updatedAt?.toDate() || null,
              submittedAt: submission.submittedAt?.toDate() || null,
            };
          })
        );

        // Combine and sort by most recent (submittedAt or updatedAt)
        const allForms = [...draftsWithClient, ...submissionsWithClient].sort((a, b) => {
          const aDate = a.submittedAt || a.updatedAt || a.createdAt;
          const bDate = b.submittedAt || b.updatedAt || b.createdAt;
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;
          return bDate.getTime() - aDate.getTime();
        });

        setSubmissions(allForms);
      } catch (err: any) {
        console.error('Error loading forms:', err);
        setError('Failed to load forms: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (role === 'admin') {
      loadForms();
    }
  }, [role]);

  const handleView = async (form: SubmissionWithClient) => {
    // Log entity formation view
    if (user && user.email && role) {
      const resourceId = form.type === 'draft' ? form.draftId : form.submissionId;
      if (resourceId) {
        logDataAccess(
          user.uid,
          user.email,
          role as 'admin' | 'staff' | 'client',
          form.type === 'draft' ? 'entity_formation_draft' : 'entity_formation_submission',
          resourceId,
          `Entity formation ${form.type} viewed: ${form.businessName || 'N/A'}`,
          true // Mark as sensitive due to potential SSN/ITIN
        ).catch((err: any) => console.error('Failed to log entity formation view:', err));
      }
    }

    if (form.type === 'draft' && form.draftId) {
      // Navigate to entity formation intake page with draftId
      navigate(`/intake/entity-formation?draftId=${form.draftId}`);
    } else if (form.type === 'submission' && form.submissionId) {
      // Navigate to admin view page
      navigate(`/admin/entity-formation/view/${form.submissionId}`);
    }
  };

  const handleDownload = async (submission: SubmissionWithClient) => {
    try {
      // Log entity formation download (contains sensitive SSN/ITIN data)
      if (user && user.email && role && submission.submissionId) {
        logPrivacyEvent(
          user.uid,
          user.email,
          role as 'admin' | 'staff' | 'client',
          'entity_formation_downloaded',
          'entity_formation_submission',
          submission.submissionId,
          `Entity formation form downloaded: ${submission.businessName || 'N/A'}`,
          { clientName: submission.clientName }
        ).catch((err: any) => console.error('Failed to log entity formation download:', err));
      }

      // Create a simple HTML representation of the form
      const htmlContent = generateFormHTML(submission);
      
      // Create a new window with the HTML
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        // Wait for content to load, then print/save as PDF
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };
      }
    } catch (err: any) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF: ' + err.message);
    }
  };

  const handleDelete = async (form: SubmissionWithClient) => {
    const formType = form.type === 'draft' ? 'draft' : 'submission';
    if (!window.confirm(`Are you sure you want to delete this ${formType} from ${form.clientName}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(form.id);
      setError('');
      setSuccess('');

      if (form.type === 'draft' && form.draftId) {
        await deleteEntityFormationDraft(form.draftId);
        setSuccess('Draft deleted successfully');
      } else if (form.type === 'submission' && form.submissionId) {
        await deleteEntityFormationSubmission(form.submissionId);
        setSuccess('Submission deleted successfully');
      }
      
      // Remove the form from the list
      setSubmissions((prev) => prev.filter((s) => s.id !== form.id));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error(`Error deleting ${formType}:`, err);
      setError(`Failed to delete ${formType}: ` + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (date: Date | null | undefined): string => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (roleLoading || loading) {
    return (
      <div>
        <Navigation />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (role !== 'admin') {
    return null; // Will redirect via useEffect
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ margin: 0 }}>Entity Formation Forms</h1>
            <p style={{ color: '#666', marginTop: '0.5rem' }}>
              View and manage all entity formation intake forms
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}>
            {success}
          </div>
        )}

        {submissions.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6',
          }}>
            <p style={{ fontSize: '1.125rem', color: '#666' }}>
              No entity formation forms found.
            </p>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #dee2e6',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Client Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Business Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Last Updated</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((form) => (
                  <tr
                    key={form.id}
                    style={{
                      borderBottom: '1px solid #dee2e6',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <td style={{ padding: '1rem' }}>
                      {form.clientName}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {form.businessName}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: form.type === 'draft' ? '#fff3cd' : '#d4edda',
                          color: form.type === 'draft' ? '#856404' : '#155724',
                        }}
                      >
                        {form.type === 'draft' ? 'Draft' : 'Submitted'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', color: '#666', fontSize: '0.875rem' }}>
                      {formatDate(form.submittedAt || form.updatedAt)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                          onClick={() => handleView(form)}
                          style={{
                            padding: '0.375rem 0.75rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                          }}
                        >
                          {form.type === 'draft' ? 'Edit' : 'View'}
                        </button>
                        {form.type === 'submission' && (
                          <button
                            onClick={() => handleDownload(form)}
                            style={{
                              padding: '0.375rem 0.75rem',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                            }}
                          >
                            Download PDF
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(form)}
                          disabled={deletingId === form.id}
                          style={{
                            padding: '0.375rem 0.75rem',
                            backgroundColor: deletingId === form.id ? '#6c757d' : '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: deletingId === form.id ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                          }}
                          title={`Delete ${form.type}`}
                        >
                          {deletingId === form.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Gets the human-readable label for a field
 */
function getFieldLabel(fieldKey: EntityIntakeFieldKey): string {
  const labels: Record<string, string> = {
    full_legal_name: 'Full Legal Name',
    preferred_name: 'Preferred Name',
    phone_number: 'Phone Number',
    email_address: 'Email Address',
    street_address: 'Street Address 1',
    street_address_2: 'Street Address 2',
    city: 'City',
    state: 'State',
    zip_code: 'ZIP Code',
    state_of_residence: 'State of Residence',
    us_citizen: 'US Citizen',
    citizenship_status: 'Citizenship Status',
    ssn_itin: 'SSN/ITIN (Optional)',
    legal_business_name: 'Legal Business Name',
    alternate_business_names: 'Alternate Business Names',
    business_description: 'Business Description',
    industry: 'Industry / Sector',
    industry_other: 'Other Industry',
    timeline: 'Timeline',
    revenue_expectations: '1st Year Revenue Expectations',
    growth_outlook: 'Growth Outlook',
    strategic_intent: 'Strategic Intent',
    strategic_intent_other: 'Other Strategic Intent',
    number_of_owners: 'Number of Owners',
    owners: 'Owners',
    management_preference: 'Management Preference',
    future_ownership_changes: 'Future Ownership Changes',
    operating_states: 'Operating States',
    operates_outside_formation_state: 'Operates Outside Formation State',
    has_employees: 'Has Employees',
    uses_contractors: 'Uses 1099 Contractors',
    sells_online: 'Sells Online',
    serves_nationally: 'Serves Nationally',
    risk_factors: 'Risk Factors',
    liability_risk: 'Liability Risk',
    has_business_bank_account: 'Has Business Bank Account',
    uses_bookkeeping_software: 'Uses Bookkeeping Software',
    bookkeeping_software_name: 'Bookkeeping Software Name',
    discussed_tax_planning: 'Discussed Tax Planning',
    owns_domain: 'Owns Domain',
    domain_name: 'Domain Name',
    website_timeline: 'Website Timeline',
    interested_in_brand_protection: 'Interested in Brand Protection',
    initial_funding: 'Initial Funding',
    initial_funding_other: 'Other Initial Funding',
    plans_to_raise_capital: 'Plans to Raise Capital',
    additional_services: 'Additional Services Interest',
    acknowledge_not_attorney_or_cpa: 'I understand that Help Keep My Money LLC provides business management consulting, is not acting as my attorney or CPA, and is not licensed to provide legal or financial advice.',
    acknowledge_entity_formation_scope: 'I understand entity formation is only one part of building a compliant and sustainable business.',
    certify_accuracy: 'I confirm the information provided is accurate to the best of my knowledge.',
    client_signature: 'Client Signature (must match Client Full Legal Name)',
    signature_date: 'Signature Date',
  };

  return labels[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Gets options for a specific section and field
 */
function getSectionOptionsPDF(section: typeof ENTITY_INTAKE_SECTIONS[number], fieldKey: string): readonly Option[] | undefined {
  if ('options' in section && section.options) {
    const options = section.options as Record<string, readonly Option[]>;
    return options[fieldKey];
  }
  return undefined;
}

/**
 * Gets the option label for a field value (for PDF generation)
 */
function getOptionLabelPDF(fieldKey: string, value: string | string[] | null, section: typeof ENTITY_INTAKE_SECTIONS[number]): string {
  if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return '(not provided)';
  }

  // Handle array values (multi-select)
  if (Array.isArray(value)) {
    const options = getSectionOptionsPDF(section, fieldKey);
    if (options) {
      return value.map((v) => options.find((o) => o.value === v)?.label || v).join(', ');
    }
    return value.join(', ');
  }

  // Handle single values
  const stringValue = String(value);

  // Check section-specific options
  const options = getSectionOptionsPDF(section, fieldKey);
  if (options) {
    return options.find((o) => o.value === stringValue)?.label || stringValue;
  }

  // Check state fields
  if (fieldKey === 'state' || fieldKey === 'state_of_residence') {
    return US_STATES.find((s) => s.code === stringValue)?.name || stringValue;
  }

  return stringValue;
}

/**
 * Renders a field value for PDF (similar to view page)
 */
function renderFieldValuePDF(fieldKey: EntityIntakeFieldKey, data: EntityIntakeDefaults, section: typeof ENTITY_INTAKE_SECTIONS[number]): string {
  const value = (data as any)[fieldKey];

  // Handle special field types - owners need detailed formatting
  if (fieldKey === 'owners') {
    if (!Array.isArray(value) || value.length === 0) {
      return '(not provided)';
    }
    return value.map((owner: any, index: number) => {
      const ownerType = OWNER_TYPE_OPTIONS.find((o) => o.value === owner.owner_type)?.label || owner.owner_type || '(not provided)';
      const parts: string[] = [];
      
      parts.push(`Owner ${index + 1}:`);
      parts.push(`Full Legal Name: ${owner.full_legal_name || '(not provided)'}`);
      parts.push(`Ownership %: ${owner.ownership_percentage || '(not provided)'}`);
      parts.push(`Owner Type: ${ownerType}`);
      parts.push(`Email: ${owner.email_address || '(not provided)'}`);
      parts.push(`Phone: ${owner.phone_number || '(not provided)'}`);
      parts.push(`Street Address: ${owner.street_address || '(not provided)'}`);
      if (owner.street_address_2) {
        parts.push(`Street Address 2: ${owner.street_address_2}`);
      }
      parts.push(`City: ${owner.city || '(not provided)'}`);
      if (owner.state) {
        const stateName = US_STATES.find((s) => s.code === owner.state)?.name || owner.state;
        parts.push(`State: ${stateName}`);
      }
      parts.push(`ZIP Code: ${owner.zip_code || '(not provided)'}`);
      if (owner.state_of_residence) {
        const stateName = US_STATES.find((s) => s.code === owner.state_of_residence)?.name || owner.state_of_residence;
        parts.push(`State of Residence: ${stateName}`);
      }
      if (owner.us_citizen !== null && owner.us_citizen !== undefined) {
        parts.push(`US Citizen: ${owner.us_citizen === 'yes' ? 'Yes' : owner.us_citizen === 'no' ? 'No' : '(not provided)'}`);
      }
      if (owner.citizenship_status) {
        parts.push(`Citizenship Status: ${owner.citizenship_status}`);
      }
      
      return parts.join('\n');
    }).join('\n\n');
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (fieldKey === 'signature_date') {
    return value || '(not provided)';
  }

  // Mask SSN/ITIN for security (show only last 4 digits if provided)
  if (fieldKey === 'ssn_itin') {
    const ssnValue = String(value || '');
    if (ssnValue) {
      const last4 = ssnValue.slice(-4);
      return `***-**-${last4}`;
    }
    return '(not provided)';
  }

  return getOptionLabelPDF(fieldKey, value, section);
}

/**
 * Generates HTML content for PDF printing
 * Uses browser print dialog to save as PDF
 * Includes all 11 sections from the entity formation form
 */
function generateFormHTML(submission: SubmissionWithClient): string {
  const data = submission.data;
  
  // Generate all sections HTML
  const sectionsHTML = ENTITY_INTAKE_SECTIONS.map((section) => {
    const visibleFields = getVisibleFieldKeysForStep(section.step, data);
    
    if (visibleFields.length === 0) {
      return '';
    }
    
    const fieldsHTML = visibleFields.map((fieldKey) => {
      const label = getFieldLabel(fieldKey);
      
      // Special handling for owners field
      if (fieldKey === 'owners') {
        const owners = (data as any).owners;
        if (!Array.isArray(owners) || owners.length === 0) {
          return `
            <div class="field">
              <span class="label">${escapeHtml(label)}:</span>
              <span class="value">(not provided)</span>
            </div>
          `;
        }
        
        const ownersHTML = owners.map((owner: any, index: number) => {
          const ownerType = OWNER_TYPE_OPTIONS.find((o) => o.value === owner.owner_type)?.label || owner.owner_type || '(not provided)';
          const stateName = owner.state ? (US_STATES.find((s) => s.code === owner.state)?.name || owner.state) : '';
          const stateOfResidenceName = owner.state_of_residence ? (US_STATES.find((s) => s.code === owner.state_of_residence)?.name || owner.state_of_residence) : '';
          
          return `
            <div style="margin-bottom: 1rem; padding: 0.75rem; background-color: #f8f9fa; border-radius: 4px; border: 1px solid #dee2e6;">
              <div style="font-weight: 600; margin-bottom: 0.5rem;">Owner ${index + 1}</div>
              <div style="display: grid; grid-template-columns: 180px 1fr; gap: 0.5rem 1rem; font-size: 0.875rem;">
                <div style="font-weight: 500; color: #666;">Full Legal Name:</div>
                <div>${escapeHtml(owner.full_legal_name || '(not provided)')}</div>
                <div style="font-weight: 500; color: #666;">Ownership %:</div>
                <div>${escapeHtml(owner.ownership_percentage || '(not provided)')}</div>
                <div style="font-weight: 500; color: #666;">Owner Type:</div>
                <div>${escapeHtml(ownerType)}</div>
                <div style="font-weight: 500; color: #666;">Email:</div>
                <div>${escapeHtml(owner.email_address || '(not provided)')}</div>
                <div style="font-weight: 500; color: #666;">Phone:</div>
                <div>${escapeHtml(owner.phone_number || '(not provided)')}</div>
                <div style="font-weight: 500; color: #666;">Street Address:</div>
                <div>${escapeHtml(owner.street_address || '(not provided)')}</div>
                ${owner.street_address_2 ? `
                <div style="font-weight: 500; color: #666;">Street Address 2:</div>
                <div>${escapeHtml(owner.street_address_2)}</div>
                ` : ''}
                <div style="font-weight: 500; color: #666;">City:</div>
                <div>${escapeHtml(owner.city || '(not provided)')}</div>
                ${stateName ? `
                <div style="font-weight: 500; color: #666;">State:</div>
                <div>${escapeHtml(stateName)}</div>
                ` : ''}
                <div style="font-weight: 500; color: #666;">ZIP Code:</div>
                <div>${escapeHtml(owner.zip_code || '(not provided)')}</div>
                ${stateOfResidenceName ? `
                <div style="font-weight: 500; color: #666;">State of Residence:</div>
                <div>${escapeHtml(stateOfResidenceName)}</div>
                ` : ''}
                ${owner.us_citizen !== null && owner.us_citizen !== undefined ? `
                <div style="font-weight: 500; color: #666;">US Citizen:</div>
                <div>${owner.us_citizen === 'yes' ? 'Yes' : owner.us_citizen === 'no' ? 'No' : '(not provided)'}</div>
                ` : ''}
                ${owner.citizenship_status ? `
                <div style="font-weight: 500; color: #666;">Citizenship Status:</div>
                <div>${escapeHtml(owner.citizenship_status)}</div>
                ` : ''}
              </div>
            </div>
          `;
        }).join('');
        
        return `
          <div class="field">
            <span class="label">${escapeHtml(label)}:</span>
          </div>
          ${ownersHTML}
        `;
      }
      
      const displayValue = renderFieldValuePDF(fieldKey, data, section);
      
      // Special handling for long acknowledgement fields
      const isLongAcknowledgement = fieldKey === 'acknowledge_not_attorney_or_cpa' || 
                                     fieldKey === 'acknowledge_entity_formation_scope';
      
      if (isLongAcknowledgement) {
        return `
          <div class="acknowledgement-field">
            <div class="label">${escapeHtml(label)}:</div>
            <div class="value">${escapeHtml(displayValue).replace(/\n/g, '<br>')}</div>
          </div>
        `;
      }
      
      return `
        <div class="field">
          <span class="label">${escapeHtml(label)}:</span>
          <span class="value">${escapeHtml(displayValue).replace(/\n/g, '<br>')}</span>
        </div>
      `;
    }).join('');
    
    return `
      <div class="section">
        <h2>Section ${section.step}: ${section.title}</h2>
        ${fieldsHTML}
      </div>
    `;
  }).join('');
  
  return `
<!DOCTYPE html>
<html>
<head>
  <title>Entity Formation Form - ${escapeHtml(submission.businessName)}</title>
  <style>
    @media print {
      @page { margin: 1cm; }
      body { margin: 0; }
    }
    body {
      font-family: Arial, sans-serif;
      padding: 20px;
      line-height: 1.6;
      color: #333;
    }
    .header {
      border-bottom: 3px solid #333;
      padding-bottom: 15px;
      margin-bottom: 30px;
    }
    h1 {
      color: #333;
      margin: 0 0 10px 0;
      font-size: 24px;
    }
    .header-info {
      font-size: 14px;
      color: #666;
    }
    h2 {
      color: #555;
      margin-top: 30px;
      margin-bottom: 15px;
      border-bottom: 2px solid #ddd;
      padding-bottom: 8px;
      font-size: 18px;
      page-break-after: avoid;
    }
    .field {
      margin-bottom: 12px;
      display: flex;
      page-break-inside: avoid;
    }
    .label {
      font-weight: bold;
      color: #333;
      min-width: 250px;
      flex-shrink: 0;
    }
    .value {
      color: #666;
      flex: 1;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
    }
    .acknowledgement-field {
      display: block;
      margin-bottom: 12px;
      page-break-inside: avoid;
    }
    .acknowledgement-field .label {
      display: block;
      font-weight: bold;
      color: #333;
      margin-bottom: 0.5rem;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
      width: 100%;
    }
    .acknowledgement-field .value {
      display: block;
      width: 100%;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
      line-height: 1.5;
      color: #666;
    }
    .section {
      margin-bottom: 30px;
      page-break-inside: avoid;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Business Entity Formation Intake Form</h1>
    <div class="header-info">
      <p><strong>Client:</strong> ${escapeHtml(submission.clientName)}</p>
      <p><strong>Business Name:</strong> ${escapeHtml(submission.businessName)}</p>
      <p><strong>Submitted:</strong> ${submission.submittedAt ? formatDate(submission.submittedAt) : 'N/A'}</p>
    </div>
  </div>
  
  ${sectionsHTML}
  
  <p style="margin-top: 40px; font-size: 0.9em; color: #666; border-top: 1px solid #ddd; padding-top: 20px;">
    <em>This document was generated from the entity formation intake form submission.</em>
  </p>
</body>
</html>
  `;
}

/**
 * Escapes HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export default AdminEntityFormationPage;

